import React, { Fragment, ReactElement, useEffect, useState } from "react";
import { gql, useQuery } from "urql";
import { OpenTimeSeries } from "@captor/openseries-ts";
import { TimeSeries, AlignMethod } from "../../../../common/src";
import { TimeSeriePlot } from "./TimeSeriePlot";

type TimeSeriesOperationPlotPropsTypes = {
    params: any[];
    startDate?: string;
    endDate: string;
    rebase?: boolean;
    alignDateRange?: boolean;
    addTypeInName?: boolean;
    useRangeSelector?: boolean;
    customMargin?: Record<string, number>;
    totalReturn?: boolean;
    /** When provided, skips internal fetch and uses this data instead */
    preloadedData?: Record<string, unknown>;
    /** When provided with preloadedData, uses these aligned series for the chart instead of building TimeSeries */
    preloadedOpenTimeseries?: OpenTimeSeries[];
    /** Optional colors per series, matched by index */
    colors?: string[];
    /** Optional style for the chart container */
    style?: React.CSSProperties;
};

export const TimeSeriesOperationPlot = ({
    params,
    startDate,
    endDate,
    rebase = false,
    alignDateRange = false,
    addTypeInName = true,
    useRangeSelector = true,
    customMargin = {},
    totalReturn = false,
    preloadedData,
    preloadedOpenTimeseries,
    colors,
    style
}: TimeSeriesOperationPlotPropsTypes): ReactElement => {
    const [animationReady, setAnimationReady] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => setAnimationReady(true));
    }, []);

    const typeIn = totalReturn ? `["DerivedTotalReturn"]` : `["Price(Close)"]`;
    const start = startDate ? ` startDate: "${startDate}",` : "";
    const query = gql`
        query timeseries {
            ${params
                .map(
                    (item, idx: number) => `
                    ts${idx}: timeserie(_id: "${item._id}") {
                            instrument {
                                _id
                                name
                                longName
                                exchanged: timeSeries(typeIn: ${typeIn}, exchanged: ${item.exchangedCurrency}, fxHedged: ${item.fxHedged},${start} endDate: "${endDate}") {
                                    type
                                    values
                                    dates
                                }
                            }
                        }
                    `
                )
                .join("")}
        }
    `;

    const [{ data: queryData, fetching: loading, error }] = useQuery({
        query,
        requestPolicy: "cache-and-network",
        pause: !!preloadedData
    });

    const data = preloadedData ?? queryData;

    if (loading) return <p>Loading time series</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (!data) return null;

    if (preloadedOpenTimeseries && preloadedOpenTimeseries.length > 0) {
        const instrumentIds = preloadedOpenTimeseries.map((ots) => ots.instrumentId || "");
        return (
            <Fragment>
                {animationReady ? (
                    <TimeSeriePlot
                        tss={preloadedOpenTimeseries}
                        instrumentIds={instrumentIds}
                        useRangeSelector={useRangeSelector}
                        customMargin={customMargin}
                        colors={colors}
                        style={style}
                    />
                ) : null}
            </Fragment>
        );
    }

    let tss: TimeSeries[] = [];
    const instrumentIds: string[] = [];

    params.forEach((value, index) => {
        const t = data[`ts${index}`];
        if (!t?.instrument?.exchanged?.[0]) return;

        const raw = t.instrument.exchanged[0];
        if (!raw?.values?.length && !(raw?.dates?.length || raw?.timestamps?.length)) return;

        let ts: TimeSeries;
        try {
            ts = TimeSeries.fromJson(raw);
        } catch {
            return;
        }
        if (!ts.__dates?.length) return;

        let name: string = t.instrument.longName ?? "";
        const firstEx = t.instrument.exchanged?.[0];
        const tType: string = firstEx?.type ?? "";
        name = addTypeInName ? name + " " + tType : name;
        const fxHedgedLbl = value.fxHedged ? " FX Hedged" : "";
        ts.name = `${name} (${value.exchangedCurrency}${fxHedgedLbl})`;

        if (rebase) {
            ts = ts.normalize(1);
        }

        tss.push(ts);
        instrumentIds.push(t.instrument._id);
    });

    if (alignDateRange) {
        let datesRange: string[] = tss[0].__dates.map((d) => d.toISOString());
        tss.forEach((ts) => {
            const dates = ts.__dates.map((d) => d.toISOString());
            datesRange = datesRange.filter((f) => dates.includes(f));
        });

        const masterTimeSerie = new TimeSeries(datesRange, [], "master");

        const alignedTss: TimeSeries[] = tss.map((ts) => {
            return TimeSeries.align(masterTimeSerie, ts, AlignMethod.Latest);
        });

        tss = alignedTss;
    }

    return (
        <Fragment>
                {animationReady ? (
                <TimeSeriePlot
                    tss={tss}
                    instrumentIds={instrumentIds}
                    useRangeSelector={useRangeSelector}
                    customMargin={customMargin}
                    colors={colors}
                    style={style}
                />
            ) : null}
        </Fragment>
    );
};
